import { client } from "./supabase";

// ─── Queries ──────────────────────────────────────────────

export const getMovimientosBancarios = async (pais, filters = {}) => {
  let query = client
    .from("movimientos_bancarios")
    .select(
      `*,
      factura_emitida:facturas_sii_emitidas(folio, razon_social_receptor),
      factura_recibida:facturas_sii_recibidas(folio, razon_social_emisor),
      factura_externa:facturas_externas(numero_documento, archivo_url),
      movimiento_caja:movimientos_caja!movimiento_bancario_id(categoria_pago)`
    )
    .limit(5000)
    .eq("pais", pais)
    .order("fecha", { ascending: false });

  if (filters.cuenta_bancaria_id) query = query.eq("cuenta_bancaria_id", filters.cuenta_bancaria_id);
  if (filters.estado) query = query.eq("estado", filters.estado);
  if (filters.tipo) query = query.eq("tipo", filters.tipo);
  if (filters.fechaDesde) query = query.gte("fecha", filters.fechaDesde);
  if (filters.fechaHasta) query = query.lte("fecha", filters.fechaHasta);
  if (filters.moneda) query = query.eq("moneda", filters.moneda);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

export const getMovimientosBancariosPendientes = async (pais) => getMovimientosBancarios(pais, { estado: "pendiente" });

// Devuelve sets de IDs de facturas SII que NO deberían ofrecerse en el modal de vinculación:
//   1. Vinculación full (movimiento bancario apunta a la factura sin cuota)
//   2. Plan de cuotas con todas las cuotas en estado distinto de 'pendiente'
export const getFacturasYaVinculadas = async (pais) => {
  const [vincRes, cobrosRes, pagosRes] = await Promise.all([
    client
      .from("movimientos_bancarios")
      .select("factura_sii_emitida_id, factura_sii_recibida_id, plan_cobro_cuota_id, plan_pago_cuota_id")
      .eq("pais", pais)
      .or("factura_sii_emitida_id.not.is.null,factura_sii_recibida_id.not.is.null")
      .range(0, 49999),
    client.from("plan_cobros_facturas").select("factura_sii_emitida_id, estado").eq("pais", pais).range(0, 49999),
    client.from("plan_pagos_facturas").select("factura_sii_recibida_id, estado").eq("pais", pais).range(0, 49999),
  ]);
  if (vincRes.error) throw vincRes.error;
  if (cobrosRes.error) throw cobrosRes.error;
  if (pagosRes.error) throw pagosRes.error;

  const emitidas = new Set();
  const recibidas = new Set();

  for (const m of vincRes.data || []) {
    if (m.factura_sii_emitida_id && !m.plan_cobro_cuota_id) emitidas.add(m.factura_sii_emitida_id);
    if (m.factura_sii_recibida_id && !m.plan_pago_cuota_id) recibidas.add(m.factura_sii_recibida_id);
  }

  const acumular = (filas, fkField) => {
    const stats = {};
    for (const c of filas || []) {
      const fid = c[fkField];
      if (!stats[fid]) stats[fid] = { total: 0, pendientes: 0 };
      stats[fid].total++;
      if (c.estado === "pendiente") stats[fid].pendientes++;
    }
    return stats;
  };

  for (const [fid, { total, pendientes }] of Object.entries(acumular(cobrosRes.data, "factura_sii_emitida_id"))) {
    if (total > 0 && pendientes === 0) emitidas.add(Number(fid));
  }
  for (const [fid, { total, pendientes }] of Object.entries(acumular(pagosRes.data, "factura_sii_recibida_id"))) {
    if (total > 0 && pendientes === 0) recibidas.add(Number(fid));
  }

  return { emitidas, recibidas };
};

export const getMovimientoBancarioById = async (id) => {
  const { data, error } = await client.from("movimientos_bancarios").select("*").eq("id", id).single();
  if (error) throw error;
  return data;
};

// ─── Importación (desde Edge Function clay-import) ────────

export const insertMovimientosBancarios = async (movimientos) => {
  if (!movimientos?.length) return { nuevos: 0, duplicados: 0 };

  const { data, error } = await client
    .from("movimientos_bancarios")
    .upsert(movimientos, { onConflict: "external_id", ignoreDuplicates: true })
    .select();

  if (error) throw error;
  const nuevos = data?.length || 0;
  return { nuevos, duplicados: movimientos.length - nuevos };
};

// ─── Matching (actualizar sugerencias) ────────────────────

export const actualizarMatch = async (id, matchData) => {
  const { data, error } = await client.from("movimientos_bancarios").update(matchData).eq("id", id).select();
  if (error) throw error;
  return data?.[0];
};

// ─── Confirmar / Ignorar ─────────────────────────────────

export const confirmarMovimiento = async (id, { movimiento_caja_id, procesado_por }) => {
  const { data, error } = await client
    .from("movimientos_bancarios")
    .update({
      estado: "procesado",
      movimiento_caja_id,
      procesado_por,
      procesado_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select();
  if (error) throw error;
  return data?.[0];
};

export const ignorarMovimiento = async (id, procesado_por) => {
  const { data, error } = await client
    .from("movimientos_bancarios")
    .update({
      estado: "ignorado",
      procesado_por,
      procesado_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select();
  if (error) throw error;
  return data?.[0];
};

export const confirmarMovimientosBulk = async (ids, procesado_por) => {
  const { data, error } = await client
    .from("movimientos_bancarios")
    .update({
      estado: "procesado",
      procesado_por,
      procesado_at: new Date().toISOString(),
    })
    .in("id", ids)
    .select();
  if (error) throw error;
  return data || [];
};

// ─── Cuentas bancarias ───────────────────────────────────

export const getCuentasBancarias = async (pais) => {
  const { data, error } = await client
    .from("cuentas_bancarias")
    .select("*, bancos(nombre)")
    .eq("pais", pais)
    .eq("habilitado", true)
    .order("moneda", { ascending: true });
  if (error) throw error;
  return data || [];
};

export const actualizarSaldoCuenta = async (id, saldo, fecha) => {
  const { error } = await client
    .from("cuentas_bancarias")
    .update({ saldo_actual: saldo, saldo_fecha: fecha, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
};

// ─── Resumen ─────────────────────────────────────────────

export const getResumenMovimientos = async (pais, fechaDesde, fechaHasta) => {
  const movimientos = await getMovimientosBancarios(pais, { fechaDesde, fechaHasta });

  const ingresos = movimientos.filter((m) => m.tipo === "ingreso");
  const egresos = movimientos.filter((m) => m.tipo === "egreso");
  const pendientes = movimientos.filter((m) => m.estado === "pendiente");

  return {
    totalIngresos: ingresos.reduce((sum, m) => sum + Number(m.monto), 0),
    totalEgresos: egresos.reduce((sum, m) => sum + Number(m.monto), 0),
    cantidadIngresos: ingresos.length,
    cantidadEgresos: egresos.length,
    pendientes: pendientes.length,
    total: movimientos.length,
  };
};

export const vincularMovimientoConFactura = async ({ movimiento, factura, esCobro, cuotaId = null, pais, userId }) => {
  const facturaTable = esCobro ? "facturas_sii_emitidas" : "facturas_sii_recibidas";
  const facturaField = esCobro ? "factura_sii_emitida_id" : "factura_sii_recibida_id";

  // Paso 1 — marcar movimiento bancario como procesado
  await client
    .from("movimientos_bancarios")
    .update({
      [facturaField]: factura.id,
      estado: "procesado",
      procesado_por: userId,
      procesado_at: new Date().toISOString(),
      match_entidad_tipo: null,
      match_entidad_id: null,
      match_entidad_nombre: null,
      match_factura_tipo: null,
      match_factura_id: null,
      match_confianza: null,
      match_razon: null,
      ...(cuotaId ? (esCobro ? { plan_cobro_cuota_id: cuotaId } : { plan_pago_cuota_id: cuotaId }) : {}),
    })
    .eq("id", movimiento.id);

  // Paso 2 — escribir fecha real en factura
  if (esCobro) {
    await client.from("facturas_sii_emitidas").update({ facturafechacobro: movimiento.fecha }).eq("id", factura.id);
  }

  // Paso 3 — actualizar cuota si corresponde
  if (cuotaId) {
    const cuotaTable = esCobro ? "plan_cobros_facturas" : "plan_pagos_facturas";
    const fechaField = esCobro ? "fecha_cobro_real" : "fecha_pago_real";
    const estadoVal = esCobro ? "cobrado" : "pagado";
    await client
      .from(cuotaTable)
      .update({
        estado: estadoVal,
        [fechaField]: movimiento.fecha,
        movimiento_bancario_id: movimiento.id,
      })
      .eq("id", cuotaId);
  } else if (!esCobro) {
    await client.from("facturas_sii_recibidas").update({ plazopago: movimiento.fecha }).eq("id", factura.id);
  }

  // Paso 4 — marcar factura como vinculada
  await client
    .from(facturaTable)
    .update({
      estado_vinculacion: "vinculada",
      vinculado_por: userId,
      vinculado_at: new Date().toISOString(),
    })
    .eq("id", factura.id);

  // Paso 4b — replicar a tabla 1:N facturas_sii_vinculaciones (solo emitidas).
  // Sin esto el modal de DTE queda desincronizado (la columna estado_vinculacion dice
  // "vinculada" pero la 1:N está vacía → la factura aparece como pendiente).
  if (esCobro) {
    const { data: facturaActual } = await client
      .from("facturas_sii_emitidas")
      .select("venta_tipo, venta_ref_id, venta_ref_index")
      .eq("id", factura.id)
      .single();
    if (facturaActual?.venta_tipo && facturaActual?.venta_ref_id != null) {
      await client.from("facturas_sii_vinculaciones").upsert(
        {
          factura_sii_emitida_id: factura.id,
          venta_tipo: facturaActual.venta_tipo,
          venta_ref_id: facturaActual.venta_ref_id,
          venta_ref_index: facturaActual.venta_ref_index ?? null,
          vinculado_por: userId,
          vinculado_at: new Date().toISOString(),
        },
        { onConflict: "factura_sii_emitida_id,venta_tipo,venta_ref_id,venta_ref_index" }
      );
    }
  }

  // Paso 5 — crear movimiento_caja
  const { data: movCaja } = await client
    .from("movimientos_caja")
    .upsert(
      {
        pais,
        tipo: esCobro ? "cobro" : "pago",
        fecha: movimiento.fecha,
        monto: Math.abs(movimiento.monto),
        monto_neto: movimiento.monto_neto ?? null,
        monto_impuesto: movimiento.monto_impuesto ?? null,
        moneda: movimiento.moneda,
        estado: "confirmado",
        descripcion: movimiento.descripcion_raw,
        entidad_tipo: esCobro ? "cliente" : "proveedor",
        entidad_id: esCobro ? factura.cliente_id : factura.proveedor_id,
        entidad_nombre: esCobro ? factura.razon_social_receptor : factura.razon_social_emisor,
        categoria_pago: esCobro ? "cobro_factura" : "pago_proveedor",
        origen: "clay",
        movimiento_bancario_id: movimiento.id,
      },
      { onConflict: "movimiento_bancario_id", ignoreDuplicates: false }
    )
    .select()
    .single();

  // Paso 6 — linkear movimiento_caja al bancario
  if (movCaja?.id) {
    await client.from("movimientos_bancarios").update({ movimiento_caja_id: movCaja.id }).eq("id", movimiento.id);
  }

  // Paso 7 — tabla puente movimiento_bancario_facturas
  await client.from("movimiento_bancario_facturas").upsert(
    {
      movimiento_bancario_id: movimiento.id,
      factura_tipo: esCobro ? "sii_emitida" : "sii_recibida",
      factura_id: factura.id,
      monto: Math.abs(movimiento.monto),
    },
    { onConflict: "movimiento_bancario_id,factura_id", ignoreDuplicates: true }
  );
};
